import { useMemo, useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Cargando } from '@/shared/ui/Estados/Cargando'

import { useFaltantes, useMarcarFaltante } from './useCompras'
import type { FaltanteCompra } from '@/lib/supabase/tipos-bd'
import estilos from './PaginaCompras.module.css'

/**
 * Compras.
 *
 * Lo que una cotización aprobada dejó sin stock, agrupado por la orden que lo
 * necesita. Compras lo toma, lo consigue y marca "ya está en inventario", que
 * avisa a gerencia y programación. Lo que World Office reponga por su cuenta se
 * cierra solo y aparece como "llegó solo".
 */

const ROTULO_ESTADO: Record<FaltanteCompra['estado'], string> = {
  pendiente: 'Pendiente',
  en_gestion: 'En gestión',
  recibido: 'Recibido',
  descartado: 'Descartado',
}

export function PaginaCompras() {
  const [verCerrados, setVerCerrados] = useState(false)
  const { faltantes, cargando, error } = useFaltantes(verCerrados)
  const marcar = useMarcarFaltante()

  // Agrupados por orden de servicio: compras piensa por trabajo, no por renglón.
  const grupos = useMemo(() => {
    const mapa = new Map<string, { os: string; cliente: string | null; items: FaltanteCompra[] }>()
    for (const f of faltantes) {
      const clave = f.orden_servicio_id ?? f.cotizacion_id
      const g = mapa.get(clave) ?? {
        os: f.os_numero ?? 'Sin orden',
        cliente: f.cliente,
        items: [],
      }
      g.items.push(f)
      mapa.set(clave, g)
    }
    return [...mapa.values()]
  }, [faltantes])

  const pendientes = faltantes.filter((f) => f.estado === 'pendiente' || f.estado === 'en_gestion')

  if (cargando) return <Cargando mensaje="CALCULANDO SUMINISTROS FALTANTES..." />
  if (error) return <Aviso tipo="error">{error}</Aviso>

  return (
    <div className={estilos.pagina}>
      <div className={estilos.cabecera}>
        <div>
          <h1 className={estilos.titulo}>Compras</h1>
          <p className={estilos.subtitulo}>
            Materiales de cotizaciones aprobadas que no alcanzaban en bodega.
            {pendientes.length > 0 && ` ${pendientes.length} por resolver.`}
          </p>
        </div>

        <label className={estilos.interruptor}>
          <input
            type="checkbox"
            checked={verCerrados}
            onChange={(e) => setVerCerrados(e.target.checked)}
          />
          Ver también los resueltos
        </label>
      </div>

      {marcar.error && <Aviso tipo="error">{(marcar.error as Error).message}</Aviso>}

      {grupos.length === 0 ? (
        <div className={estilos.vacio}>
          <p className={estilos.vacioTitulo}>Nada por comprar</p>
          <p className={estilos.vacioTexto}>
            Cuando se apruebe una cotización con materiales sin stock, aparecen aquí.
          </p>
        </div>
      ) : (
        grupos.map((g) => (
          <section key={g.os + (g.cliente ?? '')} className={estilos.grupo}>
            <div className={estilos.grupoCabecera}>
              <span className={estilos.grupoOs}>{g.os}</span>
              <span className={estilos.grupoCliente}>{g.cliente ?? 'Sin cliente'}</span>
            </div>

            <ul className={estilos.lista}>
              {g.items.map((f) => (
                <li key={f.id} className={estilos.item}>
                  <div className={estilos.itemDatos}>
                    <span className={estilos.itemNombre}>{f.descripcion}</span>
                    <span className={estilos.itemDetalle}>
                      Necesita {f.cantidad_requerida} · había {f.stock_al_crear} · faltan{' '}
                      <b>{f.faltante}</b>
                      {f.estado !== 'pendiente' && (
                        <>
                          {' · '}
                          <span className={`${estilos.estado} ${estilos[f.estado]}`}>
                            {ROTULO_ESTADO[f.estado]}
                            {f.estado === 'recibido' && f.cerrado_auto && ' (llegó solo)'}
                          </span>
                        </>
                      )}
                      {(f.estado === 'pendiente' || f.estado === 'en_gestion') &&
                        f.stock_actual > 0 && (
                          <span className={estilos.stockAhora}> · hay {f.stock_actual} ahora</span>
                        )}
                    </span>
                  </div>

                  {(f.estado === 'pendiente' || f.estado === 'en_gestion') && (
                    <div className={estilos.acciones}>
                      {f.estado === 'pendiente' && (
                        <Boton
                          variante="plano"
                          onClick={() =>
                            void marcar.mutate({ id: f.id, estado: 'en_gestion' })
                          }
                          trabajando={marcar.isPending}
                        >
                          Tomar
                        </Boton>
                      )}
                      <Boton
                        variante="principal"
                        onClick={() => void marcar.mutate({ id: f.id, estado: 'recibido' })}
                        trabajando={marcar.isPending}
                      >
                        Ya está en inventario
                      </Boton>
                      <Boton
                        variante="peligro"
                        onClick={() => void marcar.mutate({ id: f.id, estado: 'descartado' })}
                        trabajando={marcar.isPending}
                      >
                        Descartar
                      </Boton>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  )
}
